import { easyEstimatorApi } from '~/api/easy-estimator-api/easyEstimatorService';
import { LOCAL_STORAGE_KEY, VERSIONS } from '~/core/constants';
import { ApiUrl } from '~/core/enums';
import { Estimate, SaveEstimateDto } from '~/core/models';
import type {
  EstimateStatus,
  SaveEstimateResponse,
} from '~/core/models/save-estimate-dto';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';
import { convertToVersion, replacerV3, reviver } from '~/core/utils';

export type EstimateQueryParams = {
  params?: Partial<{
    code: string;
    customerEmail: string;
    estimateNumber: string;
    [LOCAL_STORAGE_KEY.USER_TOKEN]?: string;
  }>;
};
export type EstimateStatusParams = {
  params?: { code?: string };
};

const estimateService = easyEstimatorApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchEstimate: builder.query<GetEstimateDto, EstimateQueryParams>({
      query: ({ params }) => ({
        url: params?.code ? ApiUrl.ESTIMATE_CODE : ApiUrl.ESTIMATE_NUMBER,
        method: 'GET',
        params,
      }),
      transformResponse: (data: GetEstimateDto) => {
        if (!data) {
          return data;
        }

        const { estimateData, ...rest } = JSON.parse(
          JSON.stringify(data),
          reviver
        );
        const convertedEstimateData = convertToVersion(
          estimateData as Estimate,
          VERSIONS.V3
        );

        return {
          ...rest,
          estimateData: convertedEstimateData,
        } as GetEstimateDto;
      },
      extraOptions: { maxRetries: 1 },
    }),
    fetchEstimateStatus: builder.query<EstimateStatus, EstimateStatusParams>({
      query: ({ params }) => ({
        url: ApiUrl.ESTIMATE_STATUS,
        method: 'GET',
        params,
      }),
    }),
    saveEstimate: builder.mutation<SaveEstimateResponse, SaveEstimateDto>({
      query: (estimateDto) => ({
        url: ApiUrl.ESTIMATE,
        method: 'POST',
        data: JSON.stringify(estimateDto, replacerV3),
      }),
    }),
    saveEstimateMultiPart: builder.mutation<SaveEstimateResponse, FormData>({
      query: (formData) => ({
        url: ApiUrl.ESTIMATE_MULTIPART,
        method: 'POST',
        data: formData,
        isMultipartForm: true,
      }),
    }),
  }),
});

export const {
  useFetchEstimateQuery,
  useFetchEstimateStatusQuery,
  useSaveEstimateMutation,
  useSaveEstimateMultiPartMutation,
} = estimateService;
